/**
 * mediaKindIcon — one source of truth for the image / video / audio "kind" glyph,
 * so the empty-widget placeholder, the far-zoom section wireframe (svgPreview),
 * and page/palette previews all draw the SAME icon per kind (replacing the old
 * emoji). A pure, zero-dependency leaf like waveformSvg.ts.
 *
 * Returns the INNER markup of a 24×24 viewBox (no `<svg>` wrapper) with color +
 * stroke width baked in explicitly (no reliance on inheritance / `currentColor`),
 * so it works both inline in the DOM (React widgets, color: currentColor) and
 * inside svgPreview's `data:` URIs (which can't resolve currentColor), where the
 * caller passes a concrete color and a stroke width pre-divided by its scale.
 */
export type MediaIconKind = "image" | "video" | "audio";

export interface MediaIconOpts {
  /** Stroke/fill color. Default "currentColor" (DOM inline use). */
  readonly color?: string;
  /** Stroke width in viewBox units. For a scaled `<g>` embed, pass 2 / scale so
   *  the rendered stroke stays ~2px. Default 2. */
  readonly strokeWidth?: number;
}

/** Inner SVG markup (paths only) for a media kind, in a 0 0 24 24 box. */
export function mediaKindIcon(
  kind: MediaIconKind,
  opts: MediaIconOpts = {},
): string {
  const c = opts.color ?? "currentColor";
  const sw = opts.strokeWidth ?? 2;
  const S = `fill="none" stroke="${c}" stroke-width="${sw}" stroke-linecap="round" stroke-linejoin="round"`;
  switch (kind) {
    case "video":
      // A screen frame + a filled play triangle (mirrors the video wireframe).
      return (
        `<rect x="3" y="4.5" width="18" height="15" rx="2.5" ${S}/>` +
        `<path d="M10.5 9.2 15.5 12 10.5 14.8Z" fill="${c}" stroke="none"/>`
      );
    case "audio":
      // Five equalizer bars — the waveform identity.
      return (
        `<path d="M4 10.5v3" ${S}/>` +
        `<path d="M8.5 7v10" ${S}/>` +
        `<path d="M13 4.5v15" ${S}/>` +
        `<path d="M17.5 8.5v7" ${S}/>` +
        `<path d="M21 10.5v3" ${S}/>`
      );
    case "image":
    default:
      // Photo frame + sun + mountain (lucide "image").
      return (
        `<rect x="3" y="3" width="18" height="18" rx="2.5" ${S}/>` +
        `<circle cx="8.5" cy="8.5" r="1.8" ${S}/>` +
        `<path d="M21 14.5 16.5 10 7 19.5" ${S}/>`
      );
  }
}

/** Convenience: a complete standalone `<svg>` at a given size (DOM use).
 *  `size` takes px or any CSS length ("100%" — the viewBox letterboxes the
 *  glyph to the shorter side, keeping it centered and undistorted). */
export function mediaKindIconSvg(
  kind: MediaIconKind,
  size: number | string = 48,
  color = "currentColor",
): string {
  return (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" ` +
    `width="${size}" height="${size}" aria-hidden="true">` +
    `${mediaKindIcon(kind, { color })}</svg>`
  );
}
